import { z } from 'zod';
import { EvidenceRefSchema, idKindOf } from './ids';
import { NonEmptyStringSchema } from './primitives';

/**
 * Where in a source artefact a citation points, and what it means for two
 * locators to be the same place (docs/DECISIONS.md ZD-013, ZD-015, ZD-024).
 *
 * This lives in its own module rather than in `./ids` for the reason
 * `./provenance` does: it stopped being one field and became a vocabulary with
 * rules. `./ids` answers "what is a well-formed identifier"; this answers "what
 * is a well-formed location, and when do two of them resolve to the same unit".
 * The second question is the one ZD-015 rests on — a citation nothing can
 * resolve is not a citation — so it is worth its own file.
 */

// ---------------------------------------------------------------------------
// Regions
// ---------------------------------------------------------------------------

export const LOCATOR_REGION_KINDS = ['id', 'box'] as const;
export const LocatorRegionKindSchema = z.enum(LOCATOR_REGION_KINDS);
export type LocatorRegionKind = z.infer<typeof LocatorRegionKindSchema>;

/**
 * A named region: our own diagram generator's `region_id`, or a label a human
 * would recognise. An identifier, not a measurement.
 */
export const NamedRegionSchema = z.object({
  kind: z.literal('id'),
  id: NonEmptyStringSchema,
});
export type NamedRegion = z.infer<typeof NamedRegionSchema>;

/**
 * A rectangle, as **normalised fractions of the page or image**, origin top-left
 * (docs/DECISIONS.md ZD-024).
 *
 * Not pixels. A pixel box is meaningless without the dimensions it was measured
 * against, and a locator does not carry those; it also does not survive the same
 * document being re-rendered at another DPI, which would silently invalidate
 * every citation already stored against it. Fractions are stable under
 * re-rendering, and they are what a vision model can be asked for directly.
 *
 * The `x + w <= 1` bound is a real invariant rather than defensive tidying: it
 * refuses a pixel box handed in by mistake at the point of parsing, instead of
 * storing one that resolves to nothing and is discovered months later.
 *
 * That refinement is the load-bearing half, and this note is here because the
 * measurement said so. The per-field `.max(1)` caps are **subsumed** by it —
 * with `x >= 0` and `x + w <= 1`, `w` cannot exceed 1 — so deleting one leaves
 * the suite green. They are kept because they state the domain directly at the
 * field, which is worth having, and the comment says which is which rather than
 * implying both are doing work.
 */
const BoxRegionShape = z.object({
  kind: z.literal('box'),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  w: z.number().gt(0).max(1),
  h: z.number().gt(0).max(1),
});

export const BoxRegionSchema = BoxRegionShape.superRefine((box, ctx) => {
  if (box.x + box.w > 1) {
    ctx.addIssue({
      code: 'custom',
      message: `region extends past the right edge (x + w = ${String(box.x + box.w)}); coordinates are fractions of the page, not pixels`,
      path: ['w'],
    });
  }
  if (box.y + box.h > 1) {
    ctx.addIssue({
      code: 'custom',
      message: `region extends past the bottom edge (y + h = ${String(box.y + box.h)}); coordinates are fractions of the page, not pixels`,
      path: ['h'],
    });
  }
});
export type BoxRegion = z.infer<typeof BoxRegionShape>;

export const LocatorRegionSchema = z.discriminatedUnion('kind', [
  NamedRegionSchema,
  BoxRegionSchema,
]);
export type LocatorRegion = z.infer<typeof LocatorRegionSchema>;

/**
 * Thrown when a region carries a `kind` no comparison rule handles.
 *
 * Deliberately not a `default:` returning `false` (docs/DECISIONS.md ZD-024). A
 * silent `false` here surfaces months later as a citation that will not resolve,
 * with nothing to say the comparator simply did not recognise the shape it was
 * given — and `resolveCitation` returning `null` is *defined* to mean "the
 * document does not have that location", which would then be a lie. The
 * compile-time `never` assignment catches a new region kind at build time; this
 * catches one that arrived as data.
 */
export class UnhandledRegionKindError extends Error {
  constructor(region: never) {
    const kind = (region as { kind?: unknown }).kind;
    super(
      `locator region kind ${JSON.stringify(kind)} has no comparison rule; add one to regionsMatch rather than letting citations fail to resolve`,
    );
    this.name = 'UnhandledRegionKindError';
  }
}

/**
 * How much two boxes must overlap to be treated as the same region.
 *
 * Intersection-over-union rather than centroid containment, because IoU
 * penalises a box that is merely *near* the right one and a box that swallows
 * half the diagram; centroid-in-box accepts both.
 *
 * 0.5 is the conventional detection threshold and is chosen here for a specific
 * reason: two annotators drawing the same box by hand typically agree well above
 * it, while two *adjacent* elements in a diagram — the case that actually
 * matters, where an extractor cites the neighbouring box — overlap far below it.
 * It is deliberately not tuned against the corpus: the corpus's boxes come from
 * our own generator, so tuning to them would measure the generator.
 *
 * Named rather than written at the comparison site so that changing it is a
 * visible decision with this reasoning attached to it.
 */
export const REGION_MATCH_MIN_IOU = 0.5;

/** Intersection over union of two normalised boxes. 0 when they do not overlap. */
export function regionIou(a: BoxRegion, b: BoxRegion): number {
  const left = Math.max(a.x, b.x);
  const top = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.w, b.x + b.w);
  const bottom = Math.min(a.y + a.h, b.y + b.h);

  const overlap = Math.max(0, right - left) * Math.max(0, bottom - top);
  const union = a.w * a.h + b.w * b.h - overlap;
  return union <= 0 ? 0 : overlap / union;
}

/**
 * Whether two regions name the same place.
 *
 * An `id` never matches a `box`. They are different claims — one names a region,
 * the other draws a rectangle — and treating a name as evidence about geometry
 * (or the reverse) would let a citation resolve against something nobody checked.
 */
export function regionsMatch(a: LocatorRegion | null, b: LocatorRegion | null): boolean {
  if (a === null || b === null) return a === null && b === null;

  switch (a.kind) {
    case 'id':
      return b.kind === 'id' && a.id === b.id;
    case 'box':
      return b.kind === 'box' && regionIou(a, b) >= REGION_MATCH_MIN_IOU;
    default: {
      const unhandled: never = a;
      throw new UnhandledRegionKindError(unhandled);
    }
  }
}

// ---------------------------------------------------------------------------
// Locators
// ---------------------------------------------------------------------------

/**
 * Where in the source artefact a citation points (docs/DECISIONS.md ZD-013).
 *
 * At least one of the three must be populated (ZD-015). All-null satisfied the
 * type while carrying no information, which is the same trapdoor as a null
 * locator one level down: it would let extraction claim a citation it cannot
 * actually show anyone. `span` is non-empty when present for the same reason —
 * `span: ''` locates nothing — and `region` is a structured value that cannot be
 * empty by construction (ZD-024).
 */
const LocatorShape = z.object({
  page: z.number().int().positive().nullable(),
  region: LocatorRegionSchema.nullable(),
  span: NonEmptyStringSchema.nullable(),
});

export const LocatorSchema = LocatorShape.refine(
  (locator) => locator.page !== null || locator.region !== null || locator.span !== null,
  { message: 'a locator must locate something: set at least one of page, region or span' },
);
export type Locator = z.infer<typeof LocatorSchema>;

/**
 * Whether two locators point at the same place.
 *
 * `page` and `span` are exact; `region` follows ZD-024's matching rule, which is
 * geometric for boxes. This is what `resolveCitation` uses to find the unit a
 * citation names, so "the same place" has to mean the same thing at both ends —
 * which is why the rule lives beside the schema rather than in the indexer.
 */
export function locatorsMatch(a: Locator, b: Locator): boolean {
  return a.page === b.page && a.span === b.span && regionsMatch(a.region, b.region);
}

/** True when a locator carries no information at all — the ZD-015 trapdoor. */
export function locatorLocatesSomething(locator: Locator | null): boolean {
  return (
    locator !== null && (locator.page !== null || locator.region !== null || locator.span !== null)
  );
}

/**
 * A locator in words: "page 4", "lines L105-L108", "the region box-yard-controller".
 *
 * **Here rather than in `@zevik/packs`, and it is the existing argument rather
 * than a new one** (docs/DECISIONS.md ZD-112). `locatorsMatch` above says the
 * rule lives beside the schema rather than in the indexer, because "the same
 * place" has to mean the same thing at both ends. So does "where this came
 * from": a citation's locator is read back to a reviewer at the gate (§11.3's
 * *"show me where"*) and to a client in the report, and those two must not
 * describe one page differently.
 *
 * It lived in `packs/render.ts` until `sds-12b` and depends on nothing that
 * package has — a `Locator` and two numbers. Leaving it there meant a reviewer
 * workspace could reach the sentence only by importing the whole trade-secret
 * pack asset into a browser; see `client-language.ts` for what that was measured
 * to cost. `@zevik/packs` re-exports it, so its own callers are unchanged.
 */
export function describeLocator(locator: Locator | null): string {
  if (locator === null) return 'no stated location';
  const parts: string[] = [];
  if (locator.page !== null) parts.push(`page ${String(locator.page)}`);
  if (locator.span !== null) parts.push(`lines ${locator.span}`);
  if (locator.region !== null) {
    parts.push(
      locator.region.kind === 'id'
        ? `the region ${locator.region.id}`
        : `the region at (${locator.region.x.toFixed(2)}, ${locator.region.y.toFixed(2)})`,
    );
  }
  return parts.length > 0 ? parts.join(', ') : 'no stated location';
}
/**
 * An evidence reference plus where in it the assertion lives.
 *
 * The locator is **mandatory for artefact-derived citations** and optional only
 * for answers (docs/DECISIONS.md ZD-015). The rule is enforced here rather than
 * left to callers because the alternative was measured: before this refinement,
 * 9 of the 10 artefact citations in the fixture set carried `locator: null` and
 * the whole suite was green. Both the reviewer UI's "show me where" (§11.3) and
 * report language of the form "the HLD states this on page 4" (§15) are
 * impossible without it, so a citation that cannot say where is not a citation.
 *
 * The branch is decidable from the id prefix, so no caller has to declare which
 * kind of ref it is holding.
 */
export const EvidenceCitationSchema = z
  .object({
    ref: EvidenceRefSchema,
    locator: LocatorSchema.nullable(),
  })
  .superRefine((citation, ctx) => {
    if (idKindOf(citation.ref) === 'artefact' && citation.locator === null) {
      ctx.addIssue({
        code: 'custom',
        message: `citation of artefact ${citation.ref} must carry a locator: a document-derived fact has to be able to say where in the document it came from`,
        path: ['locator'],
      });
    }
  });
export type EvidenceCitation = z.infer<typeof EvidenceCitationSchema>;
